import {
  StreamIdleTimeoutError,
  StreamUnavailableError,
  type AgentFrame,
} from 'src/data/api/streamAgent';

export interface AstraMessage {
  id: string;
  role: 'user' | 'agent';
  content: string;
  timestamp: number;
  isError?: boolean;
  /**
   * Streamed model reasoning ("thinking"). Absent/empty when extended thinking
   * is disabled for the stage — the panel simply omits the reasoning section then.
   */
  reasoning?: string;
  /** True while the agent turn is still streaming in. */
  isStreaming?: boolean;
  /**
   * True when the stream failed after some answer text had already arrived. The partial
   * answer is kept and shown, with a subtle "response was interrupted" note so a cut-off
   * reply is never mistaken for a complete one.
   */
  interrupted?: boolean;
  /**
   * True when the user stopped the turn themselves (via the stop control). Any answer text
   * already streamed is kept; the panel shows a neutral "you stopped this response" note —
   * standard chat affordance — so a deliberate stop reads differently from an error.
   */
  stoppedByUser?: boolean;
}

export interface AstraState {
  isOpen: boolean;
  sessionId: string | null;
  messages: AstraMessage[];
  isLoading: boolean;
  error: string | null;
}

export const INITIAL_ASTRA_STATE: AstraState = {
  isOpen: false,
  sessionId: null,
  messages: [],
  isLoading: false,
  error: null,
};

/** Session id accepted by the Starlight proxy (`^[A-Za-z0-9-_]+$`, 8–200 chars). */
export function createSessionId(): string {
  return `astra-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

let turnSequence = 0;

/**
 * Ids for one turn's user and agent messages. A per-process sequence keeps them
 * unique even when two turns start in the same millisecond, which would
 * otherwise merge the second answer into the first agent bubble.
 */
export function createTurnMessageIds(timestamp: number): { user: string; agent: string } {
  turnSequence += 1;
  const stem = `msg-${timestamp}-${turnSequence}`;
  return { user: `${stem}-user`, agent: `${stem}-agent` };
}

/** Apply one streamed frame to the in-progress agent message. */
function reduceFrame(message: AstraMessage, frame: AgentFrame): AstraMessage {
  switch (frame.type) {
    case 'token':
      return { ...message, content: message.content + frame.text };
    case 'reasoning':
      return { ...message, reasoning: (message.reasoning ?? '') + frame.text };
    case 'done':
      return { ...message, isStreaming: false };
    // `tool` frames are consumed by the stream but not surfaced in the panel.
    default:
      return message;
  }
}

/** A fresh agent message for this turn, stamped now. */
function newAgentMessage(
  agentMsgId: string,
  extras: Partial<AstraMessage>,
): AstraMessage {
  return { id: agentMsgId, role: 'agent', content: '', timestamp: Date.now(), ...extras };
}

/**
 * Rewrites the agent message for `agentMsgId` with `update`, or — when the turn
 * hasn't produced one yet — appends `create()` as that message. Every settle
 * path (frame, stop, failure) needs both branches: the agent bubble is created
 * lazily on the first frame so the global "Thinking…" indicator shows until the
 * stream actually produces output.
 */
function upsertAgentMessage(
  messages: readonly AstraMessage[],
  agentMsgId: string,
  update: (message: AstraMessage) => AstraMessage,
  create: () => AstraMessage,
): AstraMessage[] {
  if (messages.some((m) => m.id === agentMsgId)) {
    return messages.map((m) => (m.id === agentMsgId ? update(m) : m));
  }
  return [...messages, create()];
}

/** The user's turn, appended when a send is accepted. */
export function appendUserMessage(
  state: AstraState,
  userMessage: AstraMessage,
  sessionId: string,
): AstraState {
  return {
    ...state,
    sessionId,
    messages: [...state.messages, userMessage],
    isLoading: true,
    error: null,
  };
}

/** A streamed frame lands on the (lazily created) agent message. */
export function applyAgentFrame(
  state: AstraState,
  agentMsgId: string,
  frame: AgentFrame,
): AstraState {
  return {
    ...state,
    isLoading: false,
    messages: upsertAgentMessage(
      state.messages,
      agentMsgId,
      (m) => reduceFrame(m, frame),
      () =>
        reduceFrame(
          newAgentMessage(agentMsgId, { reasoning: '', isStreaming: true }),
          frame,
        ),
    ),
  };
}

/** The stream closed normally; clear the streaming flag even without a `done` frame. */
export function settleAgentTurn(state: AstraState, agentMsgId: string): AstraState {
  return {
    ...state,
    isLoading: false,
    messages: state.messages.map((m) =>
      m.id === agentMsgId ? { ...m, isStreaming: false } : m,
    ),
  };
}

/**
 * The user stopped the turn. Keep any partial answer and flag it; if nothing
 * had streamed yet, add a placeholder so the panel's stopped note still shows —
 * a deliberate stop is always acknowledged.
 */
export function markStoppedByUser(state: AstraState, agentMsgId: string): AstraState {
  return {
    ...state,
    isLoading: false,
    error: null,
    messages: upsertAgentMessage(
      state.messages,
      agentMsgId,
      (m) => ({ ...m, isStreaming: false, stoppedByUser: true }),
      () => newAgentMessage(agentMsgId, { stoppedByUser: true }),
    ),
  };
}

/** User-facing copy for a failed turn, by failure class. */
export function describeStreamError(err: unknown): string {
  if (err instanceof StreamIdleTimeoutError) {
    return 'Astra is taking longer than expected. Please try again.';
  }
  if (err instanceof StreamUnavailableError) {
    return 'Astra is temporarily unavailable. Please try again shortly.';
  }
  return 'Something went wrong. Please try again.';
}

/**
 * The stream failed. A partial answer is kept and flagged as interrupted; only
 * when nothing streamed at all does the agent bubble become an error bubble.
 */
export function markStreamFailed(
  state: AstraState,
  agentMsgId: string,
  errorMessage: string,
): AstraState {
  return {
    ...state,
    isLoading: false,
    error: errorMessage,
    messages: upsertAgentMessage(
      state.messages,
      agentMsgId,
      (m) =>
        m.content
          ? { ...m, isStreaming: false, interrupted: true }
          : { ...m, isStreaming: false, isError: true, content: errorMessage },
      () => newAgentMessage(agentMsgId, { content: errorMessage, isError: true }),
    ),
  };
}
